import React, { useEffect, useRef, useState } from 'react';

import { CloseOutlined, LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';

import { MODAL_FOOTER_BUTTON_SIZE, MODAL_WIDTHS } from '@/shared/lib/constants';
import type { Library } from '@/shared/types';
import { BaseModal } from '@/shared/ui/BaseModal';
import { Button } from '@/shared/ui/Button';
import { Icon } from '@/shared/ui/Icon';

import { useUploadLibraryMutation } from '@/entities/dag';
import { uploadModule } from '@/entities/langserver';
import type { ModuleTarget } from '@/entities/langserver/api/types';

import {
  findDependentLibraries,
  getRequiredDependencies,
  type LibraryDependencyMap,
  normalizeImportToPackageName,
  parseImportsFromPythonCode,
  validateImportDependencies,
} from './libraryDependencyUtils';
import styles from './LibraryManager.module.css';

const DUPLICATE_LIBRARY_UPLOAD_MESSAGE = '파일 불러오기 실패 (중복) : 같은 파일명의 파일은 업로드할 수 없습니다';

const normalizeFilenameKey = (name: string): string => name.trim().toLowerCase();

const isCustomLibraryDuplicate = (
  fileName: string,
  executionFiles: Record<string, string> | undefined,
  uploadedModules: string[],
  installedLibraries: Library[]
): boolean => {
  const trimmed = fileName.trim();
  if (!trimmed.toLowerCase().endsWith('.py')) return false;

  const moduleBase = trimmed.replace(/\.py$/i, '');
  const nameKey = normalizeFilenameKey(trimmed);

  const existingFileKey =
    executionFiles && Object.keys(executionFiles).find((key) => normalizeFilenameKey(key) === nameKey);
  if (existingFileKey) {
    return true;
  }

  if (uploadedModules.some((m) => m.trim().toLowerCase() === moduleBase.toLowerCase())) {
    return true;
  }

  const installedMatch = installedLibraries.find((l) => l.name.trim().toLowerCase() === moduleBase.toLowerCase());
  if (installedMatch) {
    return true;
  }

  return false;
};

type LibraryManagerProps = {
  target: ModuleTarget;
  clientId: string;
  installedLibraries: Library[];
  availableLibraries: string[];
  uploadedModules: string[];
  libraryDisplayNames?: Record<string, string>;
  onInstall: (library: string, libraryInfo?: { type: 'custom' | 'standard'; content?: string }) => void;
  onUninstall: (library: string) => void;
  disabled?: boolean;
  onFileUpload?: (fileName: string, fileContent: string) => void;
  onExecutionFilesUpdate?: (files: Record<string, string>) => void;
  executionFiles?: Record<string, string>;
  onLibraryLog?: (message: string) => void;
};

export const LibraryManager: React.FC<LibraryManagerProps> = ({
  target,
  clientId,
  installedLibraries,
  availableLibraries,
  uploadedModules,
  libraryDisplayNames = {},
  onInstall,
  onUninstall,
  disabled = false,
  onFileUpload,
  onExecutionFilesUpdate,
  executionFiles,
  onLibraryLog,
}) => {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileContent, setFileContent] = useState('');
  const [validationStatus, setValidationStatus] = useState<'idle' | 'validating' | 'success' | 'error'>('idle');
  const [validationMessage, setValidationMessage] = useState('');
  const [togglingLibraries, setTogglingLibraries] = useState<Set<string>>(new Set());
  const [parsedImports, setParsedImports] = useState<string[]>([]);
  const [dependencyMap, setDependencyMap] = useState<LibraryDependencyMap>(() => {
    const stored = localStorage.getItem(`libraryDependencies_${target}_${clientId}`);
    return stored ? JSON.parse(stored) : {};
  });
  const { mutateAsync: uploadDagLibrary } = useUploadLibraryMutation({ showNotification: false });

  useEffect(() => {
    if (!executionFiles || Object.keys(executionFiles).length === 0) {
      return;
    }

    setDependencyMap((prevMap) => {
      const newEntries: LibraryDependencyMap = {};
      Object.entries(executionFiles).forEach(([filename, content]) => {
        try {
          const imports = parseImportsFromPythonCode(content);
          const normalizedDeps = imports
            .map((imp) => normalizeImportToPackageName(imp, availableLibraries))
            .filter((packageName) => availableLibraries.includes(packageName));

          if (normalizedDeps.length > 0) {
            const libraryName = filename.replace(/\.py$/, '');
            newEntries[libraryName] = normalizedDeps;
          }
        } catch (error) {
          console.error(`Failed to parse dependencies for ${filename}:`, error);
        }
      });

      return { ...prevMap, ...newEntries };
    });
  }, [executionFiles, availableLibraries]);

  const handlePlusClick = () => {
    setValidationMessage('파일을 선택하여 업로드 가능 여부를 확인해 주세요');
    setUploadModalOpen(true);
  };

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setSelectedFile(file);
    setValidationStatus('validating');
    setValidationMessage('파일을 검증하고 있습니다...');

    if (!file.name.endsWith('.py')) {
      setValidationStatus('error');
      setValidationMessage('Python 파일(.py)만 업로드 가능합니다.');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    try {
      const content = await file.text();
      setFileContent(content);

      const imports = parseImportsFromPythonCode(content);
      setParsedImports(imports);

      if (isCustomLibraryDuplicate(file.name, executionFiles, uploadedModules, installedLibraries)) {
        setValidationStatus('error');
        setValidationMessage(DUPLICATE_LIBRARY_UPLOAD_MESSAGE);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      const validation = validateImportDependencies(imports, availableLibraries);

      if (!validation.isValid) {
        setValidationStatus('error');
        setValidationMessage(
          `사용 불가능한 라이브러리가 import되어 있습니다: ${validation.missingPackages.join(', ')}`
        );
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      setValidationStatus('success');
      setValidationMessage('파일 검증이 완료되었습니다. 업로드를 진행해주세요.');
    } catch {
      setValidationStatus('error');
      setValidationMessage('파일을 읽는 중 오류가 발생했습니다.');
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !fileContent || validationStatus !== 'success') return;

    try {
      setValidationStatus('validating');
      setValidationMessage('파일을 업로드하고 있습니다...');

      const uploadResult = await uploadModule(target, clientId, selectedFile);

      await queryClient.invalidateQueries({
        queryKey: ['langserver', 'modules', target, clientId],
      });

      const moduleName = uploadResult.name.replace(/\.py$/, '');

      const normalizedDependencies = parsedImports
        .map((imp) => normalizeImportToPackageName(imp, availableLibraries))
        .filter((packageName) => availableLibraries.includes(packageName));

      const newDependencyMap = {
        ...dependencyMap,
        [moduleName]: normalizedDependencies,
      };
      setDependencyMap(newDependencyMap);
      localStorage.setItem(`libraryDependencies_${target}_${clientId}`, JSON.stringify(newDependencyMap));

      if (onExecutionFilesUpdate) {
        const updatedExecutionFiles = {
          ...(executionFiles || {}),
          [selectedFile.name]: fileContent,
        };
        onExecutionFilesUpdate(updatedExecutionFiles);
      }

      if (onFileUpload) {
        onFileUpload(selectedFile.name, fileContent);
      }

      if (target === 'dag') {
        try {
          await uploadDagLibrary({
            filename: selectedFile.name,
            content: fileContent,
          });
        } catch (error) {
          console.error('Failed to register DAG library:', error);
          message.warning('커스텀 스크립트는 업로드되었지만 DAG 전역 라이브러리 등록은 실패했습니다.');
        }
      }

      setUploadModalOpen(false);
      resetUploadState();

      if (onLibraryLog) {
        onLibraryLog(`Library file ${selectedFile.name} was successfully added and validated.`);
      }

      if (target === 'run') {
        message.success(`${moduleName} 모듈이 업로드되었습니다.`);

        onInstall(moduleName, { type: 'custom', content: fileContent });

        const installedLibraryNames = installedLibraries.map((l) => l.name);
        const requiredDeps = getRequiredDependencies(moduleName, newDependencyMap, installedLibraryNames);

        for (const dep of requiredDeps) {
          onInstall(dep, { type: 'standard' });
        }
      } else {
        message.success(`${uploadResult.name} 모듈이 업로드되었습니다. 사용 가능한 라이브러리에서 설치해주세요.`);
      }
    } catch (error) {
      console.error('Upload failed:', error);
      const errorMessage =
        error instanceof Error ? `파일 업로드 실패: ${error.message}` : '파일 업로드 중 오류가 발생했습니다.';
      setValidationStatus('error');
      setValidationMessage(errorMessage);
    }
  };

  const handleUploadModalClose = () => {
    setUploadModalOpen(false);
    resetUploadState();
  };

  const resetUploadState = () => {
    setSelectedFile(null);
    setFileContent('');
    setValidationStatus('idle');
    setValidationMessage('파일을 선택하여 업로드 가능 여부를 확인해 주세요');
    setParsedImports([]);
  };

  const handleUninstallClick = (lib: Library, event: React.MouseEvent) => {
    event.stopPropagation();

    if (lib.type === 'standard') {
      const installedCustomLibraries = installedLibraries.filter((l) => l.type === 'custom').map((l) => l.name);

      const dependents = findDependentLibraries(lib.name, installedCustomLibraries, dependencyMap);

      if (dependents.length > 0) {
        message.warning(`${dependents.join(', ')}에서 사용 중인 ${lib.name} 라이브러리를 제거할 수 없습니다.`);
        return;
      }
    }

    onUninstall(lib.name);

    if (onLibraryLog) {
      if (lib.type === 'custom') {
        onLibraryLog(`Library file ${lib.name}.py 제거가 완료되었습니다.`);
      } else {
        onLibraryLog(`Library ${lib.name} 제거가 완료되었습니다.`);
      }
    }
  };

  const handleInstallClick = (lib: string) => {
    setTogglingLibraries((prev) => new Set(prev).add(lib));

    onInstall(lib);

    const isCustomModule = uploadedModules.includes(lib);
    if (isCustomModule) {
      if (onLibraryLog) {
        onLibraryLog(`Library file ${lib}.py was successfully added and validated.`);
      }

      const installedLibraryNames = installedLibraries.map((l) => l.name);
      const requiredDeps = getRequiredDependencies(lib, dependencyMap, installedLibraryNames);

      for (const dep of requiredDeps) {
        onInstall(dep);
      }
    } else {
      if (onLibraryLog) {
        onLibraryLog(`Library ${lib} was successfully added.`);
      }
    }

    setTogglingLibraries((prev) => {
      const next = new Set(prev);
      next.delete(lib);
      return next;
    });
  };

  const installedLibraryNames = installedLibraries.map((lib) => lib.name);

  const getBannerClassname = () => {
    if (validationStatus === 'idle') return styles.uploadStatusBanner;
    return `${styles.uploadStatusBanner} ${styles[`uploadStatusBanner_${validationStatus}`]}`;
  };

  const renderStatusIcon = () => {
    if (validationStatus === 'validating') {
      return <LoadingOutlined className={styles.uploadStatusIcon} style={{ fontSize: '16px' }} />;
    }
    if (validationStatus === 'success') {
      return <Icon name="check-circle" size={16} className={styles.uploadStatusIcon} />;
    }
    if (validationStatus === 'error') {
      return <Icon name="error" size={16} className={styles.uploadStatusIcon} />;
    }
    return <Icon name="upload" size={16} className={styles.uploadStatusIcon} />;
  };

  return (
    <div className={styles.wrapper}>
      <input ref={fileInputRef} type="file" accept=".py" onChange={handleFileChange} style={{ display: 'none' }} />

      {installedLibraries.length > 0 && (
        <div className={styles.section}>
          <div className={styles.title}>설치된 라이브러리</div>
          <div className={styles.tagGroup}>
            {installedLibraries.map((lib) => {
              const displayName = libraryDisplayNames[lib.name] || lib.name;

              return (
                <div
                  key={lib.name}
                  className={`${styles.tag} ${lib.type === 'custom' ? styles.tagCustom : styles.tagStandard}`}
                  onClick={lib.type === 'custom' && !disabled ? (e) => handleUninstallClick(lib, e) : undefined}
                  style={lib.type === 'custom' && !disabled ? { cursor: 'pointer' } : undefined}>
                  {lib.type === 'custom' ? (
                    <>
                      <Icon name="report" size={16} />
                      <span>{displayName}</span>
                      <CloseOutlined
                        role="button"
                        aria-label={`${displayName} 제거`}
                        tabIndex={disabled ? -1 : 0}
                        style={{ fontSize: '10px', marginLeft: '4px', cursor: disabled ? 'not-allowed' : 'pointer' }}
                        onClick={
                          disabled
                            ? undefined
                            : (e) => {
                                e.stopPropagation();
                                handleUninstallClick(lib, e);
                              }
                        }
                        onKeyDown={
                          disabled
                            ? undefined
                            : (e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  handleUninstallClick(lib, e as unknown as React.MouseEvent);
                                }
                              }
                        }
                      />
                    </>
                  ) : (
                    <>
                      <span
                        onClick={disabled ? undefined : (e) => handleUninstallClick(lib, e as React.MouseEvent)}
                        style={{ cursor: disabled ? 'default' : 'pointer' }}>
                        {displayName}
                      </span>
                      <CloseOutlined
                        role="button"
                        aria-label={`${displayName} 제거`}
                        tabIndex={disabled ? -1 : 0}
                        style={{ fontSize: '10px', marginLeft: '4px', cursor: disabled ? 'not-allowed' : 'pointer' }}
                        onClick={disabled ? undefined : (e) => handleUninstallClick(lib, e)}
                        onKeyDown={
                          disabled
                            ? undefined
                            : (e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  handleUninstallClick(lib, e as unknown as React.MouseEvent);
                                }
                              }
                        }
                      />
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className={styles.section}>
        <div className={styles.title}>사용 가능한 라이브러리</div>
        <div className={styles.tagGroup}>
          {availableLibraries
            .filter((lib) => !installedLibraryNames.includes(lib))
            .map((lib) => {
              const isToggling = togglingLibraries.has(lib);
              return (
                <button
                  key={lib}
                  type="button"
                  className={`${styles.tag} ${styles.tagAvailable}`}
                  disabled={disabled || isToggling}
                  onClick={() => handleInstallClick(lib)}
                  title={`${lib} 설치`}>
                  {isToggling ? (
                    <LoadingOutlined style={{ fontSize: '12px' }} />
                  ) : (
                    <PlusOutlined style={{ fontSize: '12px' }} />
                  )}
                  {lib}
                </button>
              );
            })}
          {uploadedModules
            .filter((lib) => !installedLibraryNames.includes(lib))
            .map((lib) => {
              const isToggling = togglingLibraries.has(lib);
              return (
                <button
                  key={lib}
                  type="button"
                  className={`${styles.tag} ${styles.tagAvailable}`}
                  disabled={disabled || isToggling}
                  onClick={() => handleInstallClick(lib)}
                  title={`${lib} 설치`}>
                  {isToggling ? (
                    <LoadingOutlined style={{ fontSize: '12px' }} />
                  ) : (
                    <PlusOutlined style={{ fontSize: '12px' }} />
                  )}
                  {lib}
                </button>
              );
            })}
          <button
            type="button"
            className={styles.uploadButton}
            onClick={handlePlusClick}
            disabled={disabled}
            title="커스텀 스크립트 추가"
            aria-label="커스텀 스크립트 추가">
            <PlusOutlined style={{ fontSize: '12px' }} aria-hidden="true" />
          </button>
        </div>
      </div>

      <BaseModal
        title="라이브러리 파일 업로드"
        open={uploadModalOpen}
        onClose={handleUploadModalClose}
        centered
        width={MODAL_WIDTHS.COMPACT_FORM}
        aria-labelledby="upload-modal-title"
        footer={[
          <Button
            key="cancel"
            variant="outlined"
            color="standard"
            onClick={handleUploadModalClose}
            {...MODAL_FOOTER_BUTTON_SIZE}>
            취소
          </Button>,
          <Button
            key="upload"
            variant="filled"
            color="standard"
            onClick={handleUpload}
            disabled={validationStatus !== 'success' || !selectedFile}
            aria-label="파일 업로드"
            {...MODAL_FOOTER_BUTTON_SIZE}>
            업로드
          </Button>,
        ]}>
        <div className={styles.uploadModalContent}>
          <div className={styles.uploadRow}>
            <div className={styles.fileNameBox}>{selectedFile ? selectedFile.name : '파일을 선택해주세요'}</div>
            <Button
              prefix={<Icon name="upload" size={16} />}
              onClick={handleFileSelect}
              className={styles.fileSelectButton}>
              불러오기
            </Button>
          </div>
          <div className={getBannerClassname()}>
            {renderStatusIcon()}
            <span className={styles.uploadStatusText}>
              {validationMessage || '파일을 선택하여 업로드 가능 여부를 확인해 주세요'}
            </span>
          </div>
        </div>
      </BaseModal>
    </div>
  );
};
